import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  FaBuilding,
  FaFileContract,
  FaMapMarkerAlt,
  FaUserFriends,
  FaProjectDiagram,
  FaUserEdit,
} from 'react-icons/fa';
import { db } from '../../config/firebaseConfig';
import InfoCardModal, {
  InfoItem,
  InfoSection,
  InfoGrid,
  StatGrid,
  display,
  formatDate,
  initialsFrom,
  isEmptyValue,
} from '../../components/InfoCard/InfoCardModal';
import styles from '../../components/InfoCard/InfoCardModal.module.css';

const customerName = (customer) =>
  customer?.['Başlık'] || customer?.['Müşteri Adı'] || '';

const CustomerCardModal = ({ customer, onClose, onEdit }) => {
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState({
    addresses: [],
    personnel: [],
    tariffCount: 0,
    projects: [],
  });

  useEffect(() => {
    if (!customer?.id) return;
    let cancelled = false;

    const fetchRelated = async () => {
      setLoading(true);
      try {
        const name = customerName(customer);
        const [addressSnap, personnelSnap, tariffSnap, projectSnap] = await Promise.all([
          getDocs(query(collection(db, 'CustomerAddresses'), where('customerId', '==', customer.id))),
          getDocs(query(collection(db, 'CustomerAuthorizedPersonnel'), where('customerId', '==', customer.id))),
          getDocs(query(collection(db, 'CustomerTariffs'), where('customerId', '==', customer.id))),
          // Projeler müşteriye isim üzerinden bağlanıyor (Projects.customer = Customers.Başlık)
          name
            ? getDocs(query(collection(db, 'Projects'), where('customer', '==', name)))
            : Promise.resolve({ docs: [] }),
        ]);

        if (cancelled) return;

        setRelated({
          addresses: addressSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
          personnel: personnelSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
          tariffCount: tariffSnap.docs.length,
          projects: projectSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        });
      } catch (error) {
        console.error('Error fetching customer card data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRelated();
    return () => {
      cancelled = true;
    };
  }, [customer]);

  if (!customer) return null;

  const name = customerName(customer);
  const isActive = String(customer['Durum'] || '').toLowerCase() === 'aktif';
  const defaultAddress =
    related.addresses.find((a) => a.isDefault) || related.addresses[0] || null;
  const activeProjects = related.projects.filter(
    (p) => (p.projectStatus || 'Aktif') === 'Aktif'
  ).length;

  return (
    <InfoCardModal
      ariaLabel="Müşteri Kartı"
      title={name}
      subtitle={display(customer['Bilgi'])}
      initials={initialsFrom(name)}
      status={{
        label: isActive ? 'Aktif' : 'İnaktif',
        tone: isActive ? 'active' : 'inactive',
      }}
      chips={[
        customer['Döviz Tipi'],
        customer['Yurtiçi mi?'] === 'Evet' ? 'Yurtiçi' : 'Yurtdışı',
      ]}
      onClose={onClose}
      onEdit={onEdit}
      editIcon={<FaUserEdit />}
    >
      <InfoSection title="Özet" icon={FaProjectDiagram}>
        <StatGrid
          loading={loading}
          stats={[
            { label: 'Toplam Proje', value: related.projects.length },
            { label: 'Aktif Proje', value: activeProjects },
            { label: 'Yetkili Kişi', value: related.personnel.length },
            { label: 'Tanımlı Tarife', value: related.tariffCount },
          ]}
        />
      </InfoSection>

      <InfoSection title="Kurum Bilgileri" icon={FaBuilding}>
        <InfoGrid>
          <InfoItem label="Müşteri Adı" value={display(name)} copyable />
          <InfoItem label="Vergi Numarası" value={display(customer['Vergi Numarası'])} copyable />
          <InfoItem label="Yurtiçi mi?" value={display(customer['Yurtiçi mi?'])} />
          <InfoItem label="Döviz Tipi" value={display(customer['Döviz Tipi'])} />
          <InfoItem
            label="Müşteri Hizmet Num."
            value={display(customer['Müşteri Hizmet Num.'])}
            href={
              !isEmptyValue(customer['Müşteri Hizmet Num.'])
                ? `tel:${customer['Müşteri Hizmet Num.']}`
                : null
            }
            copyable
          />
          <InfoItem label="Durum" value={display(customer['Durum'])} />
          <InfoItem label="Bilgi" value={display(customer['Bilgi'])} wide />
        </InfoGrid>
      </InfoSection>

      <InfoSection title="Anlaşma Bilgileri" icon={FaFileContract}>
        <InfoGrid>
          <InfoItem
            label="Ana Anlaşma Numarası"
            value={display(customer['Ana Anlaşma Numarası'])}
            copyable
          />
          <InfoItem label="Ana Anlaşma Tarihi" value={formatDate(customer['Ana Anlaşma Tarihi'])} />
          <InfoItem label="Teslim Tarihi" value={formatDate(customer['Teslim Tarihi'])} />
          <InfoItem
            label="Ödeme Periyodu"
            value={
              isEmptyValue(customer['Ödeme Periyodu'])
                ? '—'
                : `${customer['Ödeme Periyodu']} gün`
            }
          />
        </InfoGrid>
      </InfoSection>

      <InfoSection title={`Adresler (${related.addresses.length})`} icon={FaMapMarkerAlt}>
        {loading ? (
          <p className={styles.emptyText}>Yükleniyor...</p>
        ) : defaultAddress ? (
          <InfoGrid columns={2}>
            <InfoItem
              label={defaultAddress.isDefault ? 'Varsayılan Adres' : 'Adres'}
              value={display(defaultAddress.title)}
            />
            <InfoItem
              label="İl / İlçe"
              value={display(
                [defaultAddress.city, defaultAddress.district].filter(Boolean).join(' / ')
              )}
            />
            <InfoItem label="Açık Adres" value={display(defaultAddress.address)} wide />
          </InfoGrid>
        ) : (
          <p className={styles.emptyText}>Kayıtlı adres bulunmuyor.</p>
        )}
      </InfoSection>

      <InfoSection title={`Yetkili Kişiler (${related.personnel.length})`} icon={FaUserFriends}>
        {loading ? (
          <p className={styles.emptyText}>Yükleniyor...</p>
        ) : related.personnel.length === 0 ? (
          <p className={styles.emptyText}>Kayıtlı yetkili kişi bulunmuyor.</p>
        ) : (
          <InfoGrid columns={2}>
            {related.personnel.slice(0, 4).map((person) => (
              <InfoItem
                key={person.id}
                label={display(person.position) !== '—' ? person.position : 'Yetkili'}
                value={display(`${person.firstName || ''} ${person.lastName || ''}`.trim())}
                hint={display(person.phone) !== '—' ? person.phone : null}
              />
            ))}
          </InfoGrid>
        )}
      </InfoSection>
    </InfoCardModal>
  );
};

export default CustomerCardModal;
